import { useState, useEffect, useCallback } from "react";

// ── Onboarding Wizard ─────────────────────────────────────────────────────────
// Drop this component into App.js and show it when onboarding_complete is false
//
// Usage in App.js:
//   import Onboarding from './Onboarding';
//   if (user && !user.onboardingComplete) return <Onboarding user={user} onComplete={handleOnboardingComplete} />;

const API_BASE = process.env.REACT_APP_API_URL || '';

// ── School Search ─────────────────────────────────────────────────────────────
function SchoolSearch({ onSelect, selected }) {
  const [query, setQuery]     = useState(selected?.name || '');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen]       = useState(false);

  const search = useCallback(async (q) => {
    if (q.length < 2) { setResults([]); return; }
    setLoading(true);
    try {
      const resp = await fetch(`${API_BASE}/api/schools/search?q=${encodeURIComponent(q)}`);
      const data = await resp.json();
      setResults(data.schools || []);
      setOpen(true);
    } catch (e) {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => search(query), 300);
    return () => clearTimeout(t);
  }, [query, search]);

  const handleSelect = (school) => {
    setQuery(school.name);
    setResults([]);
    setOpen(false);
    onSelect(school);
  };

  return (
    <div style={{ position: 'relative' }}>
      <div style={styles.inputWrapper}>
        <span style={styles.inputIcon}>🏫</span>
        <input
          style={styles.input}
          type="text"
          placeholder="Search by school name or postcode..."
          value={query}
          onChange={e => { setQuery(e.target.value); if (selected) onSelect(null); }}
          onFocus={() => results.length > 0 && setOpen(true)}
        />
        {loading && <span style={styles.inputSuffix}>⏳</span>}
        {selected && <span style={styles.inputSuffix}>✅</span>}
      </div>
      {open && results.length > 0 && (
        <div style={styles.dropdown}>
          {results.map(s => (
            <button key={s.id} style={styles.dropdownItem} onClick={() => handleSelect(s)}>
              <div style={styles.schoolName}>{s.name}</div>
              <div style={styles.schoolMeta}>{s.town} · {s.postcode} · {s.local_authority}</div>
            </button>
          ))}
        </div>
      )}
      {open && results.length === 0 && query.length >= 2 && !loading && (
        <div style={styles.dropdown}>
          <div style={{ padding: '12px 16px', color: '#888', fontSize: '0.85rem' }}>
            No schools found — try a different name or postcode
          </div>
        </div>
      )}
    </div>
  );
}

// ── Step components ───────────────────────────────────────────────────────────

function StepRole({ value, onChange }) {
  return (
    <div>
      <h2 style={styles.stepTitle}>Welcome to 67maths! 🎉</h2>
      <p style={styles.stepSubtitle}>Let's get you set up. Are you a student or a teacher?</p>
      <div style={styles.roleGrid}>
        {[
          { value: 'student', emoji: '🎒', label: 'Student', desc: 'I want to practise maths' },
          { value: 'teacher', emoji: '👩‍🏫', label: 'Teacher', desc: 'I manage a class' },
        ].map(r => (
          <button
            key={r.value}
            style={{ ...styles.roleCard, ...(value === r.value ? styles.roleCardActive : {}) }}
            onClick={() => onChange(r.value)}
          >
            <span style={styles.roleEmoji}>{r.emoji}</span>
            <span style={styles.roleLabel}>{r.label}</span>
            <span style={styles.roleDesc}>{r.desc}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function StepYear({ value, onChange, role }) {
  const years = role === 'teacher'
    ? ['1', '2', '3', '4', '5', '6', 'Mixed']
    : ['1', '2', '3', '4', '5', '6'];

  return (
    <div>
      <h2 style={styles.stepTitle}>
        {role === 'teacher' ? 'Which year group do you teach? 📚' : 'What year are you in? 📚'}
      </h2>
      <p style={styles.stepSubtitle}>
        {role === 'teacher' ? 'This helps us show the right questions for your class.' : 'We\'ll set the right questions for your year group.'}
      </p>
      <div style={styles.yearGrid}>
        {years.map(y => (
          <button
            key={y}
            style={{ ...styles.yearBtn, ...(value === y ? styles.yearBtnActive : {}) }}
            onClick={() => onChange(y)}
          >
            {y === 'Mixed' ? '🔀 Mixed' : `Year ${y}`}
          </button>
        ))}
      </div>
    </div>
  );
}

function StepSchool({ school, onSelect, role, className, onClassNameChange }) {
  return (
    <div>
      <h2 style={styles.stepTitle}>
        {role === 'teacher' ? 'Your school 🏫' : 'Which school do you go to? 🏫'}
      </h2>
      <p style={styles.stepSubtitle}>
        {role === 'teacher'
          ? 'Connect your account to your school so students can find your class.'
          : 'Part of your subscription helps raise money for your school!'}
      </p>
      <SchoolSearch onSelect={onSelect} selected={school} />
      {school && (
        <div style={styles.schoolSelected}>
          <div style={styles.schoolSelectedName}>{school.name}</div>
          <div style={styles.schoolSelectedMeta}>{school.town} · {school.postcode}</div>
        </div>
      )}
      {role === 'teacher' && (
        <div style={{ marginTop: 24 }}>
          <label style={styles.label}>Class name</label>
          <div style={styles.inputWrapper}>
            <span style={styles.inputIcon}>📋</span>
            <input
              style={styles.input}
              type="text"
              placeholder="e.g. 4B, Hedgehogs, Year 4 Maths"
              value={className}
              onChange={e => onClassNameChange(e.target.value)}
            />
          </div>
        </div>
      )}
      <button
        style={{ ...styles.skipBtn }}
        onClick={() => onSelect({ skip: true })}
      >
        Skip for now
      </button>
    </div>
  );
}

function StepJoinClass({ joinCode, onChange }) {
  return (
    <div>
      <h2 style={styles.stepTitle}>Join your class 🔑</h2>
      <p style={styles.stepSubtitle}>
        Ask your teacher for the 6-letter class code. You can also skip this and join later.
      </p>
      <div style={styles.inputWrapper}>
        <span style={styles.inputIcon}>🔑</span>
        <input
          style={{ ...styles.input, textTransform: 'uppercase', letterSpacing: '0.2em', fontSize: '1.2rem', fontWeight: 700 }}
          type="text"
          placeholder="ABC123"
          value={joinCode}
          onChange={e => onChange(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6))}
          maxLength={6}
        />
      </div>
      <p style={{ fontSize: '0.8rem', color: '#aaa', marginTop: 8 }}>
        Don't have a code? That's fine — skip and join later from Settings.
      </p>
      <button
        style={{ marginTop: 16, padding: '10px 20px', borderRadius: 8, border: '2px solid #e0e0e0', background: 'white', color: '#888', fontWeight: 600, cursor: 'pointer', width: '100%' }}
        onClick={() => onChange('')}
      >
        Skip — I'll join a class later
      </button>
    </div>
  );
}

function StepDone({ role, classData, joinedClass }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: '4rem', marginBottom: 16 }}>🚀</div>
      <h2 style={styles.stepTitle}>You're all set!</h2>
      {role === 'teacher' && classData && (
        <div style={styles.classCodeBox}>
          <p style={{ color: '#888', fontSize: '0.85rem', marginBottom: 8 }}>Share this code with your students:</p>
          <div style={styles.classCode}>{classData.join_code}</div>
          <p style={{ color: '#888', fontSize: '0.75rem', marginTop: 8 }}>They enter this when they sign up</p>
        </div>
      )}
      {role === 'student' && joinedClass && (
        <div style={styles.successBanner}>
          ✅ Joined <strong>{joinedClass.name}</strong>
        </div>
      )}
      <p style={{ color: '#888', fontSize: '0.9rem', marginTop: 16 }}>
        {role === 'teacher'
          ? 'Your class dashboard is ready. Let\'s go!'
          : 'Time to practise some maths! 🎯'}
      </p>
    </div>
  );
}

// ── Main Onboarding Component ─────────────────────────────────────────────────

export default function Onboarding({ user, token, onComplete }) {
  const [step, setStep]           = useState(0);
  const [role, setRole]           = useState('');
  const [yearGroup, setYearGroup] = useState('');
  const [school, setSchool]       = useState(null);
  const [className, setClassName] = useState('');
  const [joinCode, setJoinCode]   = useState('');
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');
  const [classData, setClassData] = useState(null);
  const [joinedClass, setJoinedClass] = useState(null);

  const STEPS = role === 'teacher'
    ? ['role', 'year', 'school', 'done']
    : ['role', 'year', 'school', 'joinclass', 'done'];

  const currentStep = STEPS[step];
  const isLastStep  = step === STEPS.length - 2; // step before done

  const canProceed = () => {
    if (currentStep === 'role')      return !!role;
    if (currentStep === 'year')      return !!yearGroup;
    if (currentStep === 'school')    return true; // school is optional
    if (currentStep === 'joinclass') return true; // join code is optional
    return false;
  };

  const handleNext = async () => {
    setError('');
    if (isLastStep) {
      await handleSubmit();
    } else {
      setStep(s => s + 1);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // Get token - use prop or fall back to localStorage
      const authToken = token || localStorage.getItem('token');

      // Complete onboarding
      const resp = await fetch(`${API_BASE}/api/user/onboarding`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({
          role,
          yearGroup: yearGroup === 'Mixed' ? null : parseInt(yearGroup),
          schoolId: school?.id || null,
          className: className || null,
        })
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || 'Onboarding failed');

      setClassData(data.class);

      // Try to join class if code provided
      if (joinCode.length === 6 && role === 'student') {
        try {
          const joinResp = await fetch(`${API_BASE}/api/classes/join`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
            body: JSON.stringify({ joinCode })
          });
          const joinData = await joinResp.json();
          if (joinResp.ok) setJoinedClass(joinData.class);
        } catch (e) {
          // Non-fatal - just skip
        }
      }

      setStep(STEPS.length - 1); // go to done step
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDone = () => {
    onComplete({ role, yearGroup, school, classData });
  };

  const progressPct = (step / (STEPS.length - 1)) * 100;

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        {/* Progress bar */}
        <div style={styles.progressBar}>
          <div style={{ ...styles.progressFill, width: `${progressPct}%` }} />
        </div>

        {/* Logo */}
        <div style={styles.logo}>
          <span style={styles.logo67}>67</span>
          <span style={styles.logoMaths}>maths</span>
        </div>

        {/* Step content */}
        <div style={styles.stepContent}>
          {currentStep === 'role'      && <StepRole value={role} onChange={setRole} />}
          {currentStep === 'year'      && <StepYear value={yearGroup} onChange={setYearGroup} role={role} />}
          {currentStep === 'school'    && <StepSchool school={school} onSelect={setSchool} role={role} className={className} onClassNameChange={setClassName} />}
          {currentStep === 'joinclass' && <StepJoinClass joinCode={joinCode} onChange={setJoinCode} />}
          {currentStep === 'done'      && <StepDone role={role} classData={classData} joinedClass={joinedClass} />}
        </div>

        {error && <div style={styles.error}>{error}</div>}

        {/* Buttons */}
        <div style={styles.btnRow}>
          {step > 0 && currentStep !== 'done' && (
            <button style={styles.backBtn} onClick={() => setStep(s => s - 1)}>
              ← Back
            </button>
          )}
          {currentStep === 'done' ? (
            <button style={{ ...styles.nextBtn, flex: 1 }} onClick={handleDone}>
              Let's go! 🚀
            </button>
          ) : (
            <button
              style={{ ...styles.nextBtn, opacity: canProceed() ? 1 : 0.4 }}
              disabled={!canProceed() || loading}
              onClick={handleNext}
            >
              {loading ? 'Setting up...' : isLastStep ? 'Finish ✓' : 'Next →'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const styles = {
  overlay: {
    position: 'fixed', inset: 0,
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 9999, padding: 16,
  },
  modal: {
    background: 'white', borderRadius: 20,
    width: '100%', maxWidth: 480,
    padding: '32px 32px 24px',
    boxShadow: '0 25px 60px rgba(0,0,0,0.3)',
    position: 'relative', overflow: 'hidden',
  },
  progressBar: {
    position: 'absolute', top: 0, left: 0, right: 0,
    height: 4, background: '#f0f0f0',
  },
  progressFill: {
    height: '100%', background: 'linear-gradient(90deg, #667eea, #764ba2)',
    transition: 'width 0.4s ease', borderRadius: '0 2px 2px 0',
  },
  logo: { textAlign: 'center', marginBottom: 24 },
  logo67: { fontSize: '1.8rem', fontWeight: 900, color: '#667eea' },
  logoMaths: { fontSize: '1.8rem', fontWeight: 900, color: '#333' },
  stepContent: { minHeight: 280 },
  stepTitle: { fontSize: '1.3rem', fontWeight: 700, color: '#1a1a2e', marginBottom: 8 },
  stepSubtitle: { fontSize: '0.9rem', color: '#666', marginBottom: 24, lineHeight: 1.5 },

  // Role selection
  roleGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
  roleCard: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    padding: '20px 12px', borderRadius: 12, border: '2px solid #e8e8e8',
    background: 'white', cursor: 'pointer', transition: 'all 0.2s',
    gap: 6,
  },
  roleCardActive: { borderColor: '#667eea', background: '#f0f0ff' },
  roleEmoji: { fontSize: '2rem' },
  roleLabel: { fontWeight: 700, fontSize: '1rem', color: '#333' },
  roleDesc: { fontSize: '0.78rem', color: '#888', textAlign: 'center' },

  // Year selection
  yearGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 },
  yearBtn: {
    padding: '14px 8px', borderRadius: 10, border: '2px solid #e8e8e8',
    background: 'white', cursor: 'pointer', fontWeight: 600,
    fontSize: '0.9rem', color: '#333', transition: 'all 0.2s',
  },
  yearBtnActive: { borderColor: '#667eea', background: '#f0f0ff', color: '#667eea' },

  // School search
  label: { display: 'block', fontWeight: 600, fontSize: '0.85rem', color: '#555', marginBottom: 6 },
  inputWrapper: {
    display: 'flex', alignItems: 'center',
    border: '2px solid #e8e8e8', borderRadius: 10,
    padding: '0 12px', background: 'white',
    transition: 'border-color 0.2s',
  },
  inputIcon: { fontSize: '1.1rem', marginRight: 8, flexShrink: 0 },
  inputSuffix: { fontSize: '1rem', marginLeft: 8 },
  input: {
    flex: 1, border: 'none', outline: 'none',
    padding: '12px 0', fontSize: '0.95rem',
    background: 'transparent', color: '#333',
  },
  dropdown: {
    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
    background: 'white', border: '1px solid #e8e8e8',
    borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
    maxHeight: 240, overflowY: 'auto', marginTop: 4,
  },
  dropdownItem: {
    display: 'block', width: '100%', textAlign: 'left',
    padding: '10px 14px', border: 'none', background: 'none',
    cursor: 'pointer', transition: 'background 0.15s',
  },
  schoolName: { fontWeight: 600, fontSize: '0.9rem', color: '#333' },
  schoolMeta: { fontSize: '0.75rem', color: '#888', marginTop: 2 },
  schoolSelected: {
    marginTop: 12, padding: '10px 14px',
    background: '#f0fff4', borderRadius: 8,
    border: '1px solid #d4edda',
  },
  schoolSelectedName: { fontWeight: 600, color: '#155724' },
  schoolSelectedMeta: { fontSize: '0.8rem', color: '#388e3c', marginTop: 2 },
  skipBtn: {
    background: 'none', border: 'none', color: '#aaa',
    fontSize: '0.82rem', cursor: 'pointer', marginTop: 12,
    textDecoration: 'underline',
  },

  // Class code display
  classCodeBox: {
    background: '#f8f4ff', borderRadius: 12, padding: '16px 20px',
    margin: '16px 0', border: '1px solid #d4c5f9',
  },
  classCode: {
    fontSize: '2rem', fontWeight: 900, letterSpacing: '0.3em',
    color: '#667eea', fontFamily: 'monospace',
  },
  successBanner: {
    background: '#f0fff4', borderRadius: 8, padding: '10px 16px',
    color: '#155724', margin: '12px 0', fontSize: '0.9rem',
  },

  // Navigation
  btnRow: { display: 'flex', gap: 10, marginTop: 24 },
  backBtn: {
    padding: '12px 20px', borderRadius: 10,
    border: '2px solid #e8e8e8', background: 'white',
    color: '#666', fontWeight: 600, cursor: 'pointer',
    fontSize: '0.9rem',
  },
  nextBtn: {
    flex: 1, padding: '13px 20px', borderRadius: 10,
    background: 'linear-gradient(135deg, #667eea, #764ba2)',
    border: 'none', color: 'white',
    fontWeight: 700, cursor: 'pointer', fontSize: '0.95rem',
    transition: 'opacity 0.2s',
  },
  error: {
    background: '#fff5f5', border: '1px solid #fed7d7',
    borderRadius: 8, padding: '8px 12px',
    color: '#c53030', fontSize: '0.85rem', marginTop: 12,
  },
};
